import { Injectable } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';

import { ServiceResponse } from "@common/data-models/service/service-response";
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { ApiServiceCollectionRecord } from '@common/data-models/service/api-service-collection-record';
import { ApiServiceRecord } from '@common/data-models/service/api-service-record';
import { FilterDef } from "@common/data-models/navigation/filter-def";
import { ObjectTypes } from "@common/enums/object-types";
import { ViewStateFilter } from "@common/data-models/navigation/viewstate-filter";
import { ViewStateRecordsAlias } from "@common/services/layout-state";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { DataStore } from "@common/data-models/data-store/data-store";
import { SingleViewStateRecord } from "@common/data-models/records/single-viewstate-record";
import { SearchViewStateRecord } from "@common/data-models/records/search-viewstate-record";
import { ViewStateTypes } from "@common/enums/viewstate-types";

//import { PlaybookSearchResults } from "@playbook/data-models/playbook-search-results/playbook-search-results";
import { ViewStateOptions } from "@suite/common/data-models/navigation/viewstate-options";

import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { ErrorTypes } from "@common/data-models/misc/errors";
import { ZFields } from "@common/data-models/fields/z-fields";

import { PlatformConvertor } from "@common/services/platform-convertor";
import { JournalSearchResults } from "@journal/data-models/journal-search-results/journal-search-results";
import { DraftRecord } from "@journal/data-models/records/draft-record";

@Injectable({ providedIn: 'root' })
export class JournalSearchService {
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public platformConvertor: PlatformConvertor
    ) { }

    public async getSearchResults(vs: ViewStateRecordsAlias, guid?: string): Promise<ServiceResponse<JournalSearchResults>> {
        let results = new JournalSearchResults();
        let response: HttpResponse<any> = null;

        let objectType = this.getObjectTypeByViewStateType(vs.viewStateType, guid);
        let singleViewState = <SingleViewStateRecord>vs;
        let searchViewState = <SearchViewStateRecord>vs;

        let draftRequestParams = this.buildFilterServiceParams(singleViewState.viewStateFilter, true, singleViewState.viewStateSorting, singleViewState.viewStateSortOrder, objectType, searchViewState.viewStateSearchQuery, false, guid);

        draftRequestParams["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];

        try {
            response = await this.nomWebServicesJson.post(
                "/api/v2/Search",
                draftRequestParams,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load search results!",
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`
            });
            return new ServiceResponse<JournalSearchResults>(false, results);
        }
        if (response) {
            let respObj = response.body;
            for (let p of respObj.SearchResults) {
                let serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                let wireRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<DraftRecord>(serviceRecord);
                DataStore.set(wireRecord);
                results.addDraft(DataStore.byGuid<DraftRecord>(wireRecord.guid, ""));
            }
        }
        results.objectType = objectType;
        return new ServiceResponse<JournalSearchResults>(true, results);
    }

    public async getSingleDraft(vs: ViewStateRecordsAlias, guid?: string): Promise<ServiceResponse<DraftRecord>> {
        let response: HttpResponse<any> = null;

        let objectType = this.getObjectTypeByViewStateType(vs.viewStateType, guid);
        let singleViewState = <SingleViewStateRecord>vs;
        let searchViewState = <SearchViewStateRecord>vs;

        let draftRequestParams = this.buildFilterServiceParams(singleViewState.viewStateFilter, true, singleViewState.viewStateSorting, singleViewState.viewStateSortOrder, objectType, searchViewState.viewStateSearchQuery, false, guid);

        draftRequestParams["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];

        try {
            response = await this.nomWebServicesJson.post(
                "/api/v2/Search",
                draftRequestParams,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load search results!",
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`
            });
            return new ServiceResponse<DraftRecord>(false, null);
        }
        if (response) {
            let respObj = response.body;
            for (let p of respObj.SearchResults) {
                let serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                let wireRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<DraftRecord>(serviceRecord);
                return new ServiceResponse<DraftRecord>(true, wireRecord)
            }
        }
    }

    private getObjectTypeByViewStateType(vsType: ViewStateTypes, guid?: string) {
        if (vsType == ViewStateTypes.DRAFT
            || vsType == ViewStateTypes.DRAFTSEARCH) {
            return ObjectTypes.DRAFT;
        }
        if (guid) {
            return ObjectTypes.UNASSIGNED;
        }
        return null;
    }

    protected buildFilterServiceParams(filter: ViewStateFilter, applyPrivs: boolean, sortBy: any, sortOrder: string, objectType?: number, search?: string, poller?: boolean, guid?: string): any {
        let QueryTerms: string = "";
        if (search) {
            QueryTerms = search;
        }

        filter = (filter) ? filter.deepCopy() : new ViewStateFilter();

        let zFields = new ZFields(!this.platformConvertor.useFor["search"]);
        zFields.add(
            "virtualPath",
            "\\\\" + this.nomWebServicesJson.PSTServer +
            "\\\\P_" + this.nomWebServicesJson.PSTGroup +
            "\\\\W"
        );
        //if (applyPrivs) {
        //    if (guid) {
        //        let type = this.checkForTypePrivs(ObjectTypes.UNASSIGNED)
        //        zFields.add("type__list__", type);
        //    } else {
        //        if (objectType) {
        //            let type = this.checkForTypePrivs(objectType)
        //            zFields.add("type", type);
        //        } else {
        //            let type = this.checkForTypePrivs(ObjectTypes.UNASSIGNED)
        //            zFields.add("type__list__", type);
        //        }
        //    }
        //} else {
        if (objectType) {
            zFields.add("type", String(objectType));
        }
        //}


        let queryTermTags: Array<string> = [];
        let queryTermUsers: Array<string> = [];
        let queryTermWords: Array<string> = [];

        let queryTermsArr = [];

        if (QueryTerms.startsWith("#")) {
            queryTermTags.push(QueryTerms.substr(1));
        } else { //we should support search for tags which consists of more than one word 
            queryTermsArr = QueryTerms.split(" ");
        }
        for (let term of queryTermsArr) {
            if (term.startsWith("@")) {
                queryTermUsers.push(term.substr(1));
            } else {
                queryTermWords.push(term);
            }
        }

        //if (queryTermTags.length > 0) {
        //    let values: Array<string> = [];
        //    if (filter.map[PSTFieldNames.PSTTopicTag] && filter.map[PSTFieldNames.PSTTopicTag].Value) {
        //        for (let v of (<Array<string>>filter.map[PSTFieldNames.PSTTopicTag].Value)) {
        //            if (values.indexOf(v) < 0) {
        //                values.push(v);
        //            }
        //        }
        //    }
        //    if (queryTermTags.length > 0) {
        //        for (let termTag of queryTermTags) {
        //            if (values.indexOf(termTag) < 0) {
        //                values.push(termTag);
        //            }
        //        }
        //    }
        //    if (values.length > 0) {
        //        filter.filters.push(FilterDef.create(PSTFieldNames.PSTTopicTag, "LOOKUPLIST", values));
        //    }
        //}

        if (queryTermUsers.length > 0) {
            for (let field of this.globalTablesService.FieldDefs) {
                let values: Array<string> = [];
                let isStaffIDList = this.globalTablesService.isStaffIDListFieldType(field.ID);
                if (isStaffIDList) {
                    if (queryTermUsers.length > 0) {
                        for (let termUser of queryTermUsers) {
                            let staffIDFilter = new FilterDef();
                            filter.groups.push(FilterDef.create(field.ID, "STAFFIDLIST", termUser));
                        }
                    }
                }
            }
        }
        this.fillZFieldsFromFilter(zFields, filter, guid);
        let sortFields = []
        if (!guid) {
            sortFields = this.buildSortFields(objectType, sortBy, sortOrder);
        }

        return ({
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: (queryTermWords.length == 0) ? "" : queryTermWords.join(" "),
            zFields: zFields.data,
            NOMLocations: [{
                BasePath: this.nomWebServicesJson.PSTServer + "\\",
                SearchWIP: true,
                SearchArchives: true,
                SearchTrash: false
            }],
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: "1800-02-28T22:00:00.000Z",
                ItemDateTo: "1800-02-28T22:00:00.000Z",
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: "1800-02-28T22:00:00.000Z",
                    ItemDateTo: "1800-02-28T22:00:00.000Z",
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false
                },
                ExtendedWireBriefing: true
            },
            MaxRows: this.visualStateService.ConfigSettings["MaxRows"],
            SortByRank: false,
            SortFields: sortFields,
            ExactMatch: false
        });
    }

    protected buildSortFields(objectType: any, sortByObj: ViewStateOptions, sortOrder: string): Array<{ [id: string]: string }> {
        const sortBy = sortByObj.Value;
        if (this.platformConvertor.useFor['search']) {
            if ((sortBy == PSTFieldNames.PSTTopicFileDate) || (sortBy == PSTFieldNames.PSTModTime)) {
                return [/*{ FieldName: sortBy, SortOrder: "desc" },*/ { FieldName: 'guid', SortOrder: "asc" }]
            } else {
                return [/*{ FieldName: sortBy, SortOrder: sortOrder }, */{ FieldName: 'guid', SortOrder: "asc" }]
            }
        } else {

            if ((sortBy == PSTFieldNames.PSTTopicFileDate) || (sortBy == PSTFieldNames.PSTModTime)) {
                return [/*{ FieldName: sortBy, FieldValue: "desc" },*/ { FieldName: 'guid', FieldValue: "asc" }]
            } else {
                return [/*{ FieldName: sortBy, FieldValue: sortOrder }, */{ FieldName: 'guid', FieldValue: "asc" }]
            }

        }
    }

    protected fillZFieldsFromFilter(zFields: ZFields, filterObj: ViewStateFilter, guid?: string) {
        if (filterObj && !guid) {
            for (let group of filterObj.groups) {
                let value = group.group();
                if (value) {
                    zFields.add(value.FieldName, value.FieldValue);
                }
            }

            for (let filter of filterObj.filters) {
                let value = filter.get();
                if (value) {
                    zFields.add(value.FieldName, value.FieldValue);
                }
            }
        }
        if (guid) {
            zFields.add("guid", guid);
        }

        return zFields;
    }
}
